'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';

const SECTION_ID = 'service-area-testimonial';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  },
};

interface TestimonialSectionProperties {
  testimonial: { quote: string; author: string; relationship: string };
  townName: string;
}

function TestimonialSection({
  testimonial,
  townName,
}: TestimonialSectionProperties) {
  return (
    <SectionBase id={SECTION_ID} background="bg-card">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="TESTIMONIAL"
          title="What Our Client Says"
          description={`Hear from a homeowner about their experience with Cornerstone Homes in ${townName}.`}
        />

        <motion.div
          className={cls`flex flex-col items-center gap-6 max-w-2xl mx-auto text-center`}
          {...cardMotionProps}
        >
          <Quote className="text-primary size-12 fill-primary" />

          <TypographySmall className="text-foreground leading-relaxed text-lg italic">
            &ldquo;{testimonial.quote}&rdquo;
          </TypographySmall>

          <div className="flex flex-col items-center gap-1">
            <span className="font-prata text-base text-foreground font-semibold">
              &mdash; {testimonial.author}
            </span>
            <TypographySmall className="text-muted-foreground">
              {testimonial.relationship}
            </TypographySmall>
          </div>
        </motion.div>
      </motion.div>
    </SectionBase>
  );
}

export default TestimonialSection;
